import {configContainer, statusContainer, utils} from '@wallaby-ui/core';
import {
  cloneDeep,
  difference,
  findIndex,
  forOwn,
  forEach,
  isEmpty,
  omitBy,
  startsWith,
} from 'lodash';
import {defineMessages} from 'react-intl';

import * as at from './auto_layout_action_types';
import {
  ARRAY_SCREENS_WRAPPER,
  CONFLICT_HTTP_CODE,
  TAB_SET_SCREENS_WRAPPER,
} from './auto_layout_constants';
import {screenPropertiesDefaultState} from './auto_layout_reducer';
import {
  getArrayWrapperIndex,
  getArrayWrapperLoadedIndex,
  getIndexScreenItemsData,
  getStateMetadata,
  getStateMetadataByScreenId,
} from './auto_layout_selectors';
import AutoLayoutUtils from './auto_layout_utils';
import {LIBRARY_NAME} from 'src/js/common/constants';
import AutoLayoutFetcher from 'src/js/fetchers/auto_layout_fetcher';
import AutoLayoutService from 'src/js/lib/auto_layout/auto_layout_service';
import {
  KEYWORD_DATA_ITEMS,
  KEYWORD_DATA_CURRENT_SCREEN_CONTENT,
  ScreenDataHelpers,
} from 'src/js/model/auto_layout_screen_data_helpers';
import {MiniScreensConfig} from 'src/js/model/mini_screens_config';
import {ValidationRules} from 'src/js/model/validation';
// import screenDataMock from 'src/js/model/screen_data_mock.json';
// import screenMetaDataMock from 'src/js/model/hai_screen_metadata_mock.json';
// import miniScreensConfigMock from 'src/js/model/mini_screens_config_mock.json';

const logger = utils.LogUtils.getLogger('AutoLayoutActions', LIBRARY_NAME);

/* eslint-disable max-len */
const I18N = defineMessages({
  EDITED_REMOVED_SCREEN: {
    id: 'autoLayout.wrapper.editedRemovedScreen',
    defaultMessage:
      'Je nám lito, ale jiný uživatel smazal hlášenku, kterou jste editovali. Vaše úpravy budou ztraceny.',
    description:
      'text for auto layout when trying to edit screen removed by another user',
  },
  UPDATED_INVALID_INDEX: {
    id: 'autoLayout.wrapper.updatedInvalidIndex',
    defaultMessage:
      'Uložení hlášenky není možné, protože jiný uživatel přidal / smazal hlášenku. Jeho změny musí být načteny před uložením Vaší hlášenky.',
    description:
      'text for auto layout array container when trying to update invalid index',
  },
});

/* eslint-enable max-len */

/**
 * Gets metadata for particular screen.
 * Metadata are retrieved from the server and they are cached afterwards. Next time,
 * when the metadata with the same identifier are retrieved, they are retrieved form cache,
 * without need to request them from a server again.
 *
 * @param {String} stateId Store state id.
 * @param {String} requestUrl Request url for getting server metadata.
 * @param {String} screenId ScreenId that identifies metadata.
 * @param {String} metadataMethod Request method for getting server metadata.
 * @param {Boolean} reload Reloads metadata from server.
 * @returns {Object} Metadata.
 */
export function getMetadata(
  stateId,
  requestUrl,
  screenId,
  metadataMethod,
  reload
) {
  return (dispatch, getState) => {
    let metadataPromise;
    const state = getState();
    const cachedMetadata = state[stateId].metadataCache[screenId];
    if (cachedMetadata && !reload) {
      metadataPromise = new Promise((resolve) => {
        resolve(cachedMetadata);
      });
    } else {
      metadataPromise = AutoLayoutFetcher.getMetadata(
        requestUrl,
        screenId,
        metadataMethod
      );
      // metadataPromise = new Promise((resolve) => {
      //    resolve(screenMetaDataMock);
      // });
    }
    return metadataPromise
      .then((response) => {
        const rawMetadata = omitBy(response, (value, key) =>
          startsWith(key, '$')
        );
        dispatch({
          type: at.AUTO_LAYOUT_FETCH_METADATA_SUCCESS,
          payload: {screenId, response: rawMetadata},
          stateId,
        });
      })
      .catch((error) => {
        logger.error(`AUTO_LAYOUT_FETCH_METADATA_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(error, at.AUTO_LAYOUT_FETCH_METADATA_FAILURE)
        );
        dispatch({
          type: at.AUTO_LAYOUT_FETCH_METADATA_FAILURE,
          payload: error,
          stateId,
        });
      });
  };
}

export function initData(
  stateId,
  screenId,
  data,
  isIndex = false,
  reload = false,
  requestScreenId = screenId,
  rootContentDef = null,
  contentDef = null
) {
  let content;
  const isFromDb = !isEmpty(data);
  if (isEmpty(data)) {
    if (isIndex) {
      content = ScreenDataHelpers.createEmptyIndex();
    } else {
      content = ScreenDataHelpers.createEmptyData(rootContentDef, contentDef);
    }
  }

  return (dispatch) =>
    dispatch({
      type: at.AUTO_LAYOUT_INIT_DATA,
      payload: {
        screenId,
        requestScreenId,
        response: data,
        content,
        isFromDb,
        reload,
      },
      stateId,
    });
}

export function initScreenProperties(stateId, screenId, data, layout) {
  const isFromDb = !isEmpty(data);

  return (dispatch, getState) => {
    let screenProperties = {...screenPropertiesDefaultState};
    const state = getState()[stateId];
    const currentScreenProperties = state.screenProperties[screenId];

    if (currentScreenProperties && 'isCollapsed' in currentScreenProperties) {
      // use collapsing state from cache
      screenProperties = {...currentScreenProperties};
    }
    screenProperties = {...screenProperties, isFromDb};

    if (layout && layout.isCollapsible() && !screenProperties.isEditing) {
      if (screenProperties.useDefaultCollapse) {
        const collapsedDefault = layout.getCollapsedDefault();

        // use collapsing state from screen config
        screenProperties = {
          ...screenProperties,
          useDefaultCollapse: false,
          isCollapsed: collapsedDefault,
        };
      }
    }

    return dispatch({
      type: at.AUTO_LAYOUT_INIT_SCREEN_PROPERTIES,
      payload: {screenId, screenProperties},
      stateId,
    });
  };
}

export function resetCheckedIndexState(stateId, screenId) {
  return (dispatch) =>
    dispatch({
      type: at.AUTO_LAYOUT_CHECKED_INDEX_STATE_RESET,
      payload: {screenId},
      stateId,
    });
}

export function resetConflictLastChangeAuthor(stateId, screenId) {
  return (dispatch) =>
    dispatch({
      type: at.AUTO_LAYOUT_CONFLICT_LAST_CHANGE_AUTHOR_RESET,
      payload: {screenId},
      stateId,
    });
}

/**
 * Fetch content and create a basic skeleton if content is empty.
 *
 * @param {String} stateId Store state id.
 * @param {String} requestUrl Request url for getting server data.
 * @param {Number|String} screenId
 * @param {Object} params Query params for get request.
 * @param {String} errorMessage Localized error message in case of failure.
 * @param {Boolean} isIndex Determine if the screen is index screen.
 * @param {Boolean} reload Determine if the screen data should be reloaded from server.
 * @param {ContentSchema|Object} rootContentDef root content definition
 * @param {ContentComponent|Object} contentDef content definition
 * @param {LayoutComponent|Object} layoutDef layout definition
 */
export function getData(
  stateId,
  requestUrl,
  screenId,
  params,
  errorMessage,
  isIndex = false,
  reload = false,
  rootContentDef = null,
  contentDef = null,
  layoutDef = null
) {
  const requestScreenId = isIndex
    ? AutoLayoutService.getIndexScreenId(screenId)
    : screenId;
  const screenDataPromise = AutoLayoutFetcher.getData(
    requestUrl,
    requestScreenId,
    params
  );
  // const screenDataPromise = new Promise((resolve) => {
  //    resolve(screenDataMock);
  // });
  const reducerScreenId = isIndex
    ? AutoLayoutService.getIndexScreenId(screenId, params)
    : screenId;
  return (dispatch) => {
    dispatch({
      type: at.AUTO_LAYOUT_FETCH_DATA,
      payload: {screenId: reducerScreenId},
      stateId,
    });

    return screenDataPromise
      .then((data) => {
        dispatch(
          initData(
            stateId,
            reducerScreenId,
            data,
            isIndex,
            reload,
            requestScreenId,
            rootContentDef,
            contentDef
          )
        );
        dispatch(
          initScreenProperties(stateId, reducerScreenId, data, layoutDef)
        );

        dispatch({
          type: at.AUTO_LAYOUT_FETCH_DATA_SUCCESS,
          payload: {screenId: reducerScreenId, response: data},
          stateId,
        });

        if (reload) {
          if (isIndex) {
            dispatch(resetCheckedIndexState(stateId, reducerScreenId));
          }
          dispatch(resetConflictLastChangeAuthor(stateId, reducerScreenId));
        }

        return data;
      })
      .catch((error) => {
        logger.error(`AUTO_LAYOUT_FETCH_DATA_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(error, at.AUTO_LAYOUT_FETCH_DATA_FAILURE)
        );
        dispatch({
          type: at.AUTO_LAYOUT_FETCH_DATA_FAILURE,
          payload: {screenId: reducerScreenId, error, errorMessage},
          stateId,
        });
      });
  };
}

/**
 * Validate data according to validation configuration, ex.
 * "validator": [{"required":"hai.miniScreen.hai.state.validation.required"}].
 * The order of validator is important, because the validation of the field
 * is interrupted after the first error.
 * @param stateId
 * @param screenId
 * @param data
 * @returns {Function}
 */
export function validateData(stateId, screenId, data) {
  return (dispatch, getState) => {
    const metadata = getStateMetadata(getState(), {autoLayoutStateId: stateId});
    const propMetadata = metadata.getItemContent().getProperties();
    // const warnings = {};
    const errors = {};
    const messages = {};
    const warnings = [];
    forOwn(propMetadata, (property, key) => {
      if (property.getValidators()) {
        // eslint-disable-next-line consistent-return
        forEach(property.getValidators(), (validator) => {
          const dataVal = AutoLayoutUtils.getDataVal(data, key);
          const checkCondition = validator.conditions
            ? AutoLayoutUtils.checkDataConditions(validator.conditions, data)
            : true;

          const isValid = checkCondition
            ? ValidationRules[validator.name](dataVal, validator.param)
            : true;

          if (!isValid) {
            errors[key] = true;
            messages[key] = validator.message;
            return false;
          }
        });
      }

      if (property.getArrayValidators()) {
        const indexItemsData = getIndexScreenItemsData(getState(), {
          autoLayoutStateId: stateId,
        });
        const indexItemsDataList = Array.from(Object.values(indexItemsData));
        // eslint-disable-next-line consistent-return
        forEach(property.getArrayValidators(), (arrayValidator) => {
          // check if conditions are defined, if yes we should apply this validator
          const checkCondition = arrayValidator.conditions
            ? AutoLayoutUtils.checkDataConditions(
                arrayValidator.conditions,
                data
              )
            : true;

          let isValid = true;
          if (checkCondition) {
            isValid = AutoLayoutUtils.checkArrayDataConditions(
              arrayValidator.type,
              arrayValidator.arrayConditions,
              indexItemsDataList,
              data
            );
          }
          if (!isValid) {
            if (arrayValidator.severity === 'ERROR') {
              errors[key] = true;
              messages[key] = arrayValidator.message;
            } else {
              warnings.push(arrayValidator.message);
            }
            return false;
          }
        });
      }
    });
    dispatch({
      type: at.AUTO_LAYOUT_DATA_VALIDATED,
      payload: {screenId, errors, messages, warnings},
      stateId,
    });

    return {errors, warnings};
  };
}

function getInvalidIndexError(itemsDifferences, currentDataChanges, screenId) {
  let result = '';

  if (!isEmpty(itemsDifferences.added) || !isEmpty(itemsDifferences.removed)) {
    result = I18N.UPDATED_INVALID_INDEX.id;

    const isEditedRemoved = itemsDifferences.removed.some(
      (itemId) =>
        !isEmpty(
          currentDataChanges[
            AutoLayoutService.getItemScreenId(screenId, itemId)
          ]
        )
    );
    if (isEditedRemoved) {
      result = I18N.EDITED_REMOVED_SCREEN.id;
    }
  }

  return result;
}

function getIndexItemsDifferences(oldIndexItems, newIndexItems) {
  const result = {};

  result.added = difference(newIndexItems, oldIndexItems);
  result.removed = difference(oldIndexItems, newIndexItems);

  return result;
}

export function checkCurrentIndex(
  stateId,
  requestUrl,
  screenId,
  params,
  invokeInfo = {}
) {
  const requestScreenId = AutoLayoutService.getIndexScreenId(screenId);
  const reducerScreenId = AutoLayoutService.getIndexScreenId(screenId, params);
  const {invokerScreenId} = invokeInfo;
  return (dispatch, getState) => {
    // load new index to determine error situation
    const screenDataPromise = AutoLayoutFetcher.getData(
      requestUrl,
      requestScreenId,
      params
    );
    screenDataPromise
      .then((data) => {
        const state = getState()[stateId];
        const oldIndexItems =
          state.loadedDataCache[reducerScreenId][KEYWORD_DATA_ITEMS];
        const newIndexItems = data[KEYWORD_DATA_ITEMS];
        const currentIndexItems =
          state.dataCache[reducerScreenId][KEYWORD_DATA_ITEMS];
        const itemsDifferences = getIndexItemsDifferences(
          oldIndexItems,
          newIndexItems
        );
        const currentDataChanges = state.dataChanges;
        const currentIndexItemsDifferences = getIndexItemsDifferences(
          oldIndexItems,
          currentIndexItems
        );
        // determine error situation and message
        const invalidIndexError = getInvalidIndexError(
          itemsDifferences,
          currentDataChanges,
          screenId
        );

        dispatch({
          type: at.AUTO_LAYOUT_CONFLICT_LAST_CHANGE_AUTHOR,
          payload: {
            lastChangeAuthor: ScreenDataHelpers.getLastChangeUser(data),
            screenId: invokerScreenId || reducerScreenId,
          },
          stateId,
        });

        dispatch({
          type: at.AUTO_LAYOUT_CHECKED_INDEX_STATE,
          payload: {
            screenId: reducerScreenId,
            itemsDifferences,
            currentIndexItemsDifferences,
            invalidIndexError,
            invokeInfo,
          },
          stateId,
        });
      })
      .catch(() => {
        // TODO:
      });
  };
}

/**
 * Save data of screen.
 * @param {String} stateId Store state id.
 * @param {String} requestUrl Request url for getting server data.
 * @param {Number|String} screenId
 * @param {Object} data Data to save.
 * @param {Object} params Query params for save request.
 * @param {String} errorMessage Localized error message in case of failure.
 * @param {Boolean} isIndex Determine if the screen is index screen.
 */
export function saveData(
  stateId,
  requestUrl,
  screenId,
  data,
  params,
  errorMessage,
  isIndex = false
) {
  const reducerScreenId = isIndex
    ? AutoLayoutService.getIndexScreenId(screenId, params)
    : screenId;

  return (dispatch, getState) => {
    let modifiedData = data;
    if (!isIndex) {
      const metadata = getStateMetadata(getState(), {
        autoLayoutStateId: stateId,
      });
      const propMetadata = metadata.getItemContent().getProperties();
      forOwn(propMetadata, (property, key) => {
        // delete the value of conditional fields if the condition is not satisfied
        if (property.isConditional()) {
          const checkConditions = AutoLayoutUtils.checkDataConditions(
            property.getConditions(),
            modifiedData
          );
          if (!checkConditions) {
            modifiedData.content[key] = undefined;
          }
        }

        // remove empty array items
        modifiedData = AutoLayoutUtils.removeEmptyArrayItems(
          modifiedData,
          metadata,
          key
        );
      });
    }

    dispatch({
      type: at.AUTO_LAYOUT_SAVE_DATA,
      payload: {screenId: reducerScreenId},
      stateId,
    });

    const requestScreenId = isIndex
      ? AutoLayoutService.getIndexScreenId(screenId)
      : screenId;
    return AutoLayoutFetcher.saveData(
      requestUrl,
      requestScreenId,
      modifiedData,
      params
    )
      .then(
        /** @param resultData {{_responseData : Object, _responseStatus: number}} */ (
          resultData
        ) => {
          let result = resultData;
          if (resultData._responseStatus === CONFLICT_HTTP_CODE) {
            if (isIndex) {
              // load new index to determine error situation
              dispatch(
                checkCurrentIndex(stateId, requestUrl, screenId, params)
              );
            } else {
              dispatch({
                type: at.AUTO_LAYOUT_CONFLICT_LAST_CHANGE_AUTHOR,
                payload: {
                  lastChangeAuthor: ScreenDataHelpers.getLastChangeUser(
                    resultData._responseData
                  ),
                  screenId,
                },
                stateId,
              });
            }

            const error = CONFLICT_HTTP_CODE.toString();
            dispatch({
              type: at.AUTO_LAYOUT_SAVE_DATA_FAILURE,
              payload: {
                isInvalid: true,
                screenId: reducerScreenId,
                error,
                errorMessage: ScreenDataHelpers.getErrorText(
                  resultData._responseData
                ),
              },
              stateId,
            });

            if (isIndex) {
              throw error;
            }
            result = {isConflictRequestResponse: true};
          } else {
            const newData =
              resultData &&
              resultData._responseData &&
              resultData._responseData[KEYWORD_DATA_CURRENT_SCREEN_CONTENT]
                ? resultData._responseData[KEYWORD_DATA_CURRENT_SCREEN_CONTENT]
                : modifiedData;

            dispatch({
              type: at.AUTO_LAYOUT_SAVE_DATA_SUCCESS,
              payload: {screenId: reducerScreenId, data: newData},
              stateId,
            });
          }

          return result;
        }
      )
      .catch((error) => {
        logger.error(`AUTO_LAYOUT_SAVE_DATA_FAILURE: ${error}`);

        dispatch({
          type: at.AUTO_LAYOUT_SAVE_DATA_FAILURE,
          payload: {screenId: reducerScreenId, error, errorMessage},
          stateId,
        });

        if (isIndex) {
          throw error;
        }
        return {isConflictRequestResponse: true};
      });
  };
}

/**
 * Remove data of screen.
 * @param {String} stateId Store state id.
 * @param {Number|String} screenId
 */
export function dataRemoved(stateId, screenId) {
  return (dispatch) =>
    dispatch({type: at.AUTO_LAYOUT_DATA_REMOVED, payload: {screenId}, stateId});
}

/**
 * Remove data of screen.
 * @param {String} stateId Store state id.
 * @param {String} requestUrl Request url for getting server data.
 * @param {Number|String} screenId
 * @param {Object} data Data to save.
 * @param {Object} params Query params for save request.
 * @param {String} errorMessage Localized error message in case of failure.
 */
export function removeData(
  stateId,
  requestUrl,
  screenId,
  data,
  params,
  errorMessage
) {
  return (dispatch) => {
    dispatch({type: at.AUTO_LAYOUT_DELETE_DATA, payload: {screenId}, stateId});

    return AutoLayoutFetcher.removeData(requestUrl, screenId, data, params)
      .then(
        /** @param resultData {{_responseData : Object, _responseStatus: number}} */ (
          resultData
        ) => {
          let result = resultData;
          if (resultData._responseStatus === CONFLICT_HTTP_CODE) {
            dispatch({
              type: at.AUTO_LAYOUT_CONFLICT_LAST_CHANGE_AUTHOR,
              payload: {
                lastChangeAuthor: ScreenDataHelpers.getLastChangeUser(
                  resultData._responseData
                ),
                screenId,
              },
              stateId,
            });

            const error = CONFLICT_HTTP_CODE.toString();
            dispatch({
              type: at.AUTO_LAYOUT_DELETE_DATA_FAILURE,
              payload: {
                isInvalid: true,
                screenId,
                error,
                errorMessage: ScreenDataHelpers.getErrorText(
                  resultData._responseData
                ),
              },
              stateId,
            });

            result = {isConflictRequestResponse: true};
          } else {
            dispatch({
              type: at.AUTO_LAYOUT_DELETE_DATA_SUCCESS,
              payload: {screenId},
              stateId,
            });
          }

          return result;
        }
      )
      .catch((error) => {
        logger.error(`AUTO_LAYOUT_DELETE_DATA_FAILURE: ${error}`);

        dispatch({
          type: at.AUTO_LAYOUT_DELETE_DATA_FAILURE,
          payload: {screenId, error, errorMessage},
          stateId,
        });

        return {isConflictRequestResponse: true};
      });
  };
}

export function loadAllDataSets(stateId, requestUrl, datasetIds) {
  return (dispatch) => {
    const datasetPromises = [];
    if (datasetIds) {
      datasetIds.forEach((dataSetId) => {
        datasetPromises.push(
          dispatch(
            configContainer.actions.getDataSet(
              stateId,
              requestUrl,
              dataSetId,
              false
            )
          )
        );
      });
    }
    return Promise.all(datasetPromises);
  };
}

export function loadMetadata(
  stateId,
  requestUrl,
  screenId,
  metadataMethod,
  configStateId,
  datasetUrl,
  reload = false
) {
  return (dispatch, getState) =>
    dispatch(
      getMetadata(stateId, requestUrl, screenId, metadataMethod, reload)
    ).then(() => {
      const metadata = getStateMetadataByScreenId(getState(), {
        autoLayoutStateId: stateId,
        screenId,
      });
      return dispatch(
        loadAllDataSets(configStateId, datasetUrl, metadata.getDataSetIds())
      );
    });
}

export function changeData(stateId, screenId, dataChange) {
  return (dispatch) =>
    dispatch({
      type: at.AUTO_LAYOUT_CHANGE_DATA,
      payload: {dataChange, screenId},
      stateId,
    });
}

export function changeScreenProperty(stateId, screenId, propertyChange) {
  return (dispatch) =>
    dispatch({
      type: at.AUTO_LAYOUT_CHANGE_SCREEN_PROPERTY,
      payload: {propertyChange, screenId},
      stateId,
    });
}

export function indexAddItem(stateId, screenId, itemId) {
  return (dispatch) =>
    dispatch({
      type: at.AUTO_LAYOUT_INDEX_ADD,
      payload: {itemId, screenId},
      stateId,
    });
}

export function indexRemoveItem(stateId, screenId, itemId) {
  return (dispatch) =>
    dispatch({
      type: at.AUTO_LAYOUT_INDEX_REMOVE,
      payload: {itemId, screenId},
      stateId,
    });
}

export function saveIndex(
  stateId,
  requestUrl,
  screenId,
  params,
  errorMessage,
  itemId,
  isAdded
) {
  return (dispatch, getState) => {
    const state = getState()[stateId];
    // get current loadedIndex
    const loadedIndex =
      state.loadedDataCache[
        AutoLayoutService.getIndexScreenId(screenId, params)
      ];
    const data = cloneDeep(loadedIndex) || {};
    if (isAdded) {
      if (data[KEYWORD_DATA_ITEMS]) {
        data[KEYWORD_DATA_ITEMS] = [...data[KEYWORD_DATA_ITEMS], itemId];
      }
    } else {
      const itemIdx = findIndex(
        data[KEYWORD_DATA_ITEMS],
        (item) => item === itemId
      );
      if (itemIdx !== -1) {
        data[KEYWORD_DATA_ITEMS] = [
          ...data[KEYWORD_DATA_ITEMS].slice(0, itemIdx),
          ...data[KEYWORD_DATA_ITEMS].slice(itemIdx + 1),
        ];
      }
    }

    // save index
    return Promise.all([
      dispatch(
        saveData(
          stateId,
          requestUrl,
          screenId,
          data,
          params,
          errorMessage,
          true
        )
      ),
    ]);
  };
}

export function setMiniScreensConfig(stateId, data, reload = false) {
  const config = new MiniScreensConfig(data);
  return (dispatch) =>
    dispatch({
      type: at.SET_MINI_SCREENS_CONFIG,
      payload: {config, reload},
      stateId,
    });
}

export function loadMiniScreensConfig(
  stateId,
  requestUrl,
  configStateId,
  reload = false
) {
  return (dispatch, getState) =>
    dispatch(
      configContainer.actions.getConfig(configStateId, requestUrl, true)
    ).then(() => {
      const config = getState()[configStateId].config[requestUrl];
      // const config = miniScreensConfigMock;
      return dispatch(setMiniScreensConfig(stateId, config, reload));
    });
}

export function changeSelectedScreenId(stateId, screenId, contextParams) {
  const contextParamsId = AutoLayoutService.getContextToString(contextParams);
  return (dispatch) =>
    dispatch({
      type: at.CHANGE_SELECTED_SCREEN_ID,
      payload: {screenId, contextParamsId},
      stateId,
    });
}

export function resetData(stateId, screenId) {
  return (dispatch) =>
    dispatch({
      type: at.AUTO_LAYOUT_RESET_CHANGES,
      payload: {screenId},
      stateId,
    });
}

export function setCurrentContextParams(stateId, contextParams) {
  return (dispatch) =>
    dispatch({
      type: at.SET_CURRENT_CONTEXT_PARAMS,
      payload: {contextParams},
      stateId,
    });
}

export function loadAllScreenData(
  stateId,
  config,
  screenId,
  contextParams,
  errorMessage,
  indexItems,
  isIndex = false,
  rootContentDef = null,
  contentDef = null,
  layoutDef = null
) {
  return (dispatch) => {
    const loadScreenDataPromises = [];
    forEach(indexItems, (item) => {
      loadScreenDataPromises.push(
        dispatch(
          getData(
            stateId,
            config.service.url.autoLayout,
            `${screenId}.${item}`,
            contextParams,
            errorMessage,
            isIndex,
            false,
            rootContentDef,
            contentDef,
            layoutDef
          )
        )
      );
    });
    return Promise.all(loadScreenDataPromises);
  };
}

function isIndexLayout(metadata) {
  const componentType =
    !isEmpty(metadata.getLayout()) && metadata.getLayout().getComponentType()
      ? metadata.getLayout().getComponentType().toLowerCase()
      : null;
  return (
    componentType === ARRAY_SCREENS_WRAPPER ||
    componentType === TAB_SET_SCREENS_WRAPPER
  );
}

export function loadAutoLayout(
  stateId,
  config,
  configStateId,
  contextParams,
  isDynamic,
  miniScreensConfig,
  screenId,
  errorMessage,
  reloadIndex = false,
  locallyAddedScreens = []
) {
  return (dispatch, getState) => {
    dispatch({type: at.AUTO_LAYOUT_LOAD_START, stateId});

    // LOAD CONFIGURATIONS (ONLY IF NECESSARY)
    return Promise.all([
      ...(!reloadIndex &&
      isDynamic &&
      (isEmpty(miniScreensConfig) || !miniScreensConfig.isLoaded())
        ? [
            dispatch(
              loadMiniScreensConfig(
                stateId,
                config.service.url.miniScreensConfig,
                configStateId
              )
            ),
          ]
        : []),
    ])
      .then(() =>
        // LOAD INDEPENDENT AUTO_LAYOUT DATA
        Promise.all([
          ...(reloadIndex
            ? []
            : [
                dispatch(
                  loadMetadata(
                    stateId,
                    config.service.url.autoLayout,
                    screenId,
                    config.service.url.autoLayoutMetadataMethod,
                    configStateId,
                    config.service.url.configDataSet
                  )
                ),
              ]),
        ])
      )
      .then(() => {
        // LOAD DEPENDENT AUTO_LAYOUT DATA (INDEX OR SINGLE SCREEN DATA)
        const metadata = getStateMetadataByScreenId(getState(), {
          autoLayoutStateId: stateId,
          screenId,
        });
        const isIndex = isIndexLayout(metadata);
        return Promise.all([
          dispatch(
            getData(
              stateId,
              config.service.url.autoLayout,
              screenId,
              contextParams,
              errorMessage,
              isIndex,
              reloadIndex,
              isIndex ? null : metadata.getContent(),
              isIndex ? null : metadata.getItemContent(),
              isIndex ? null : metadata.getItemLayout()
            )
          ),
        ]);
      })
      .then(() => {
        // (ON RELOAD ONLY) ADD LOCALLY ADDED SCREEN AFTER INDEX WAS RELOADED
        const metadata = getStateMetadataByScreenId(getState(), {
          autoLayoutStateId: stateId,
          screenId,
        });
        const isIndex = isIndexLayout(metadata);
        const promises = [];
        if (isIndex && reloadIndex && !isEmpty(locallyAddedScreens)) {
          locallyAddedScreens.forEach((locallyAddedScreenId) => {
            promises.push(
              dispatch(
                indexAddItem(
                  stateId,
                  AutoLayoutService.getIndexScreenId(screenId, contextParams),
                  locallyAddedScreenId
                )
              )
            );
            promises.push(
              dispatch(
                changeScreenProperty(
                  stateId,
                  AutoLayoutService.getItemScreenId(
                    screenId,
                    locallyAddedScreenId
                  ),
                  {isEditing: true}
                )
              )
            );
          });
        }
        return Promise.all(promises);
      })
      .then(() => {
        const metadata = getStateMetadataByScreenId(getState(), {
          autoLayoutStateId: stateId,
          screenId,
        });
        const isIndex = isIndexLayout(metadata);
        const promises = [];
        if (isIndex) {
          const arrayWrapperIndexSelector = reloadIndex
            ? getArrayWrapperLoadedIndex
            : getArrayWrapperIndex;
          const index = arrayWrapperIndexSelector(getState(), {
            autoLayoutStateId: stateId,
            screenId,
            currentContextParams: contextParams,
          });
          const indexItems = [
            ...index[KEYWORD_DATA_ITEMS],
            ...(reloadIndex ? locallyAddedScreens : []),
          ];
          promises.push(
            dispatch(
              loadAllScreenData(
                stateId,
                config,
                screenId,
                contextParams,
                errorMessage,
                indexItems,
                false,
                metadata.getContent(),
                metadata.getItemContent(),
                metadata.getItemLayout()
              )
            )
          );
        }
        return Promise.all(
          // LOAD AUTO_LAYOUT INDEX ITEMS DATA (IF NECESSARY)
          promises
        );
      })
      .then(() =>
        Promise.all([
          // SET CURRENT_CONTEXT_PARAMS WHEN EVERYTHING IS LOADED
          dispatch({type: at.AUTO_LAYOUT_LOAD_END, stateId}),
          dispatch(setCurrentContextParams(stateId, contextParams)),
        ])
      );
  };
}
